/**
 * NotificationBell — bell icon with badge + dropdown.
 *
 * Behavior:
 *   - Shows unread count as red badge (capped at 99+).
 *   - Click → opens dropdown with latest 20 notifications.
 *   - Click a notification: marks it as read and navigates to its `link`.
 *   - "Mark all as read" + "Clear read" actions in the header.
 *   - Bilingual: uses *_ar fields when language is 'ar'.
 *   - RTL-aware: dropdown anchors correctly in both directions.
 */
import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Bell,
  Check,
  CheckCheck,
  Trash2,
  AlertTriangle,
  AlertCircle,
  Info,
  CheckCircle2,
  CreditCard,
  Briefcase,
  FileText,
  Users,
  Settings as SettingsIcon,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  useUnreadCount,
  useNotificationsList,
  useMarkRead,
  useMarkAllRead,
  useDeleteNotification,
  useClearRead,
  type Notification,
} from '@/hooks/useNotifications';

// Map type → icon + color
const typeIcon = (type: string) => {
  const map: Record<string, { Icon: typeof Bell; cls: string }> = {
    info:     { Icon: Info,         cls: 'text-blue-500' },
    success:  { Icon: CheckCircle2, cls: 'text-green-500' },
    warning:  { Icon: AlertTriangle,cls: 'text-yellow-500' },
    error:    { Icon: AlertCircle,  cls: 'text-red-500' },
    payment:  { Icon: CreditCard,   cls: 'text-emerald-600' },
    project:  { Icon: Briefcase,    cls: 'text-indigo-500' },
    boq:      { Icon: FileText,     cls: 'text-purple-500' },
    document: { Icon: FileText,     cls: 'text-slate-500' },
    team:     { Icon: Users,        cls: 'text-pink-500' },
    system:   { Icon: SettingsIcon, cls: 'text-slate-500' },
  };
  return map[type] ?? map.info;
};

const severityRing = (severity: string) => {
  switch (severity) {
    case 'critical': return 'ring-2 ring-red-400';
    case 'high':     return 'ring-1 ring-orange-300';
    default:         return '';
  }
};

// Friendly relative time. Returns localized strings.
function timeAgo(iso: string, lang: string): string {
  const diffSec = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  const units: [number, string, string][] = [
    [60,        'second',  'ثانية'],
    [3600,      'minute',  'دقيقة'],
    [86400,     'hour',    'ساعة'],
    [604800,    'day',     'يوم'],
    [2592000,   'week',    'أسبوع'],
    [31536000,  'month',   'شهر'],
  ];
  for (let i = 0; i < units.length; i++) {
    const [limit, en, ar] = units[i];
    if (diffSec < limit) {
      const prev = i === 0 ? 1 : units[i - 1][0];
      const value = Math.max(1, Math.floor(diffSec / prev));
      if (lang === 'ar') return `منذ ${value} ${ar}${value > 1 ? '' : ''}`;
      return `${value} ${en}${value === 1 ? '' : 's'} ago`;
    }
  }
  return lang === 'ar' ? 'منذ سنوات' : 'years ago';
}

export default function NotificationBell() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const isRTL = lang === 'ar';
  const navigate = useNavigate();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const [open, setOpen] = useState(false);

  const { data: unread = 0 } = useUnreadCount();
  const { data: listData, isLoading } = useNotificationsList(
    { page: 1, page_size: 20 },
    open,
  );

  const markRead = useMarkRead();
  const markAllRead = useMarkAllRead();
  const deleteOne = useDeleteNotification();
  const clearRead = useClearRead();

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node) &&
        !buttonRef.current?.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const handleClickItem = async (n: Notification) => {
    if (!n.is_read) {
      await markRead.mutateAsync(n.id);
    }
    if (n.link) {
      setOpen(false);
      navigate(n.link);
    }
  };

  const handleMarkAll = async () => {
    if (unread === 0) return;
    await markAllRead.mutateAsync();
    toast.success(
      isRTL ? 'تم تعليم كل الإشعارات كمقروءة' : 'All notifications marked as read'
    );
  };

  const handleClearRead = async () => {
    const res = await clearRead.mutateAsync();
    toast.success(
      isRTL
        ? `تم حذف ${res.affected} إشعار مقروء`
        : `Cleared ${res.affected} read notification(s)`
    );
  };

  const handleDelete = async (id: number, e: React.MouseEvent) => {
    e.stopPropagation();
    await deleteOne.mutateAsync(id);
  };

  const items = listData?.items ?? [];
  const showBadge = unread > 0;
  const badgeText = unread > 99 ? '99+' : String(unread);

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="relative p-2 min-h-[40px] min-w-[40px] inline-flex items-center justify-center rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
        aria-label={isRTL ? 'الإشعارات' : 'Notifications'}
      >
        <Bell className="w-5 h-5 text-slate-700 dark:text-slate-200" />
        {showBadge && (
          <span
            className="absolute -top-0.5 -end-0.5 min-w-[18px] h-[18px] px-1
                       bg-red-500 text-white text-[10px] font-bold
                       rounded-full flex items-center justify-center
                       ring-2 ring-white dark:ring-slate-900"
          >
            {badgeText}
          </span>
        )}
      </button>

      {open && (
        <div
          ref={dropdownRef}
          style={isRTL ? { right: '100%', marginRight: '0.5rem', bottom: 0 } : { left: '100%', marginLeft: '0.5rem', bottom: 0 }}
          className={[
            'absolute w-[380px] max-w-[calc(100vw-2rem)]',
            'bg-white dark:bg-slate-900',
            'border border-slate-200 dark:border-slate-700',
            'rounded-xl shadow-xl z-50 overflow-hidden',
          ].join(' ')}
          dir={isRTL ? 'rtl' : 'ltr'}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 dark:border-slate-700">
            <div className="flex items-center gap-2">
              <Bell className="w-4 h-4 text-slate-600 dark:text-slate-300" />
              <h3 className="font-semibold text-slate-900 dark:text-white">
                {isRTL ? 'الإشعارات' : 'Notifications'}
              </h3>
              {unread > 0 && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300">
                  {unread} {isRTL ? 'جديد' : 'new'}
                </span>
              )}
            </div>
            <div className="flex items-center gap-1">
              {unread > 0 && (
                <button
                  onClick={handleMarkAll}
                  className="p-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-800"
                  title={isRTL ? 'تعليم الكل كمقروء' : 'Mark all as read'}
                >
                  <CheckCheck className="w-4 h-4 text-slate-600 dark:text-slate-300" />
                </button>
              )}
              <button
                onClick={handleClearRead}
                className="p-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-800"
                title={isRTL ? 'حذف المقروءة' : 'Clear read'}
              >
                <Trash2 className="w-4 h-4 text-slate-600 dark:text-slate-300" />
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="max-h-[480px] overflow-y-auto">
            {isLoading ? (
              <div className="p-8 text-center text-sm text-slate-500">
                {isRTL ? 'جاري التحميل...' : 'Loading...'}
              </div>
            ) : items.length === 0 ? (
              <div className="p-10 text-center">
                <Bell className="w-10 h-10 mx-auto text-slate-300 dark:text-slate-600 mb-2" />
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  {isRTL ? 'لا توجد إشعارات' : 'No notifications'}
                </p>
              </div>
            ) : (
              <ul className="divide-y divide-slate-100 dark:divide-slate-800">
                {items.map((n) => {
                  const { Icon, cls } = typeIcon(n.notification_type);
                  const ringCls = severityRing(n.severity);
                  const title = isRTL && n.title_ar ? n.title_ar : n.title;
                  const message = isRTL && n.message_ar ? n.message_ar : n.message;

                  return (
                    <li
                      key={n.id}
                      onClick={() => handleClickItem(n)}
                      className={[
                        'group cursor-pointer px-4 py-3',
                        'hover:bg-slate-50 dark:hover:bg-slate-800/50',
                        !n.is_read ? 'bg-blue-50/40 dark:bg-blue-950/20' : '',
                      ].join(' ')}
                    >
                      <div className="flex gap-3">
                        <div
                          className={[
                            'flex-shrink-0 w-9 h-9 rounded-full',
                            'bg-slate-100 dark:bg-slate-800',
                            'flex items-center justify-center',
                            ringCls,
                          ].join(' ')}
                        >
                          <Icon className={`w-4 h-4 ${cls}`} />
                        </div>

                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <p
                              className={[
                                'text-sm leading-tight',
                                !n.is_read
                                  ? 'font-semibold text-slate-900 dark:text-white'
                                  : 'font-medium text-slate-700 dark:text-slate-200',
                              ].join(' ')}
                            >
                              {title}
                            </p>
                            <button
                              onClick={(e) => handleDelete(n.id, e)}
                              className="opacity-0 group-hover:opacity-100 transition-opacity
                                         p-1 rounded hover:bg-slate-200 dark:hover:bg-slate-700"
                              title={isRTL ? 'حذف' : 'Delete'}
                            >
                              <X className="w-3.5 h-3.5 text-slate-500" />
                            </button>
                          </div>
                          <p className="text-xs mt-0.5 text-slate-600 dark:text-slate-400 line-clamp-2">
                            {message}
                          </p>
                          <div className="flex items-center gap-2 mt-1.5">
                            <span className="text-[11px] text-slate-400">
                              {timeAgo(n.created_at, lang)}
                            </span>
                            {!n.is_read && (
                              <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                            )}
                          </div>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {/* Footer */}
          {items.length > 0 && (
            <div className="px-4 py-2 border-t border-slate-200 dark:border-slate-700 text-center">
              <button
                onClick={() => {
                  setOpen(false);
                  navigate('/notifications');
                }}
                className="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 font-medium"
              >
                {isRTL ? 'عرض الكل' : 'View all'}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
